/**
 * Layout Field Components
 * 
 * These are non-editable fields that control form layout and structure.
 * Includes: heading, static_text, divider, spacer, section_header
 * 
 * Each field is rendered EXACTLY as in App.js to maintain visual parity.
 */

import React from 'react';
import { formatDateMDY } from '../../../../utils/dateFormat';
import { staticTextBorderStyle, staticTextImageClass } from '../../../../utils/staticTextStyle';

/**
 * Replace client and system variables in text content
 * Matches the replaceClientVariables function from App.js
 */
const replaceClientVariables = (text, client, user) => {
  if (!text || typeof text !== 'string') return typeof text === 'string' ? text : '';
  
  let result = text;
  
  // Client variables (only if client exists)
  if (client) {
    result = result
      .replace(/\{\{client_name\}\}/g, client.full_name || '')
      .replace(/\{\{client_first_name\}\}/g, client.first_name || '')
      .replace(/\{\{client_last_name\}\}/g, client.last_name || '')
      .replace(/\{\{client_dob\}\}/g, formatDateMDY(client.dob || client.date_of_birth) || '')
      .replace(/\{\{client_age\}\}/g, client.dob ? Math.floor((new Date() - new Date(client.dob)) / (365.25 * 24 * 60 * 60 * 1000)).toString() : '')
      .replace(/\{\{client_email\}\}/g, client.email || '')
      .replace(/\{\{client_phone\}\}/g, client.phone || '')
      .replace(/\{\{client_address\}\}/g, [client.address_line1 || client.address || client.street, client.address_line2, client.city, client.state, client.zipcode || client.zip || client.zip_code].filter(Boolean).join(', ') || '')
      .replace(/\{\{client_mrn\}\}/g, client.mrn || '');
  }
  
  // System/user variables
  result = result
    .replace(/\{\{today\}\}/g, new Date().toLocaleDateString())
    .replace(/\{\{current_date\}\}/g, new Date().toLocaleDateString())
    .replace(/\{\{provider_name\}\}/g, user?.full_name || user?.name || '');
  
  return result;
};

/**
 * Render a layout field based on its type
 * Returns null if field type is not a layout field
 */
export const renderLayoutField = (field, index, fieldStyle, context) => {
  const { clients = [], selectedNoteClients = [], user, selectedClient } = context;
  
  // Get the client for variable replacement
  const clientForVariables = selectedClient || clients.find(c => c.id === selectedNoteClients[0]);
  
  // Heading
  if (field.type === 'heading') {
    return (
      <div 
        key={index} 
        style={{
          ...fieldStyle,
          color: field.fontColor || 'inherit',
          textAlign: field.textAlign || 'left'
        }} 
        className={`${field.isBold !== false ? 'font-bold' : ''} ${field.isItalic ? 'italic' : ''} ${field.isUnderline ? 'underline' : ''} ${field.headingLevel === 'h1' ? 'text-2xl' : field.headingLevel === 'h3' ? 'text-base' : 'text-xl'}`}
      >
        {replaceClientVariables(field.label, clientForVariables, user)}
      </div>
    );
  }
  
  // Static text (with variable replacement)
  // Uses field.content (main content) OR field.label as fallback
  // Supports rich text styling: inline HTML formatting or legacy field-level styles
  if (field.type === 'static_text') {
    const rawText = (typeof field.content === 'string' && field.content) || (typeof field.label === 'string' && field.label) || '';
    let processedContent = replaceClientVariables(rawText, clientForVariables, user);
    
    // Container style for alignment and base font size
    const containerStyle = {
      textAlign: field.textAlign || 'left',
      fontSize: field.fontSize ? `${field.fontSize}px` : '14px'
    };
    
    // If no content, show placeholder in form builder preview
    if (!rawText) {
      return (
        <div key={index} style={fieldStyle}>
          <div className="text-sm text-gray-400 italic">
            (No static text content defined)
          </div>
        </div>
      );
    }
    
    // Check if content contains HTML tags (from rich text editor)
    const hasHtmlTags = /<[^>]+>/.test(processedContent);
    // Source-formatting newlines between tags render as blank lines under
    // whitespace-pre-wrap, ballooning list spacing — collapse them.
    if (hasHtmlTags) {
      processedContent = processedContent.replace(/>\s*\n\s*</g, '><');
    }
    
    const borderStyle = staticTextBorderStyle(field);
    const imgClass = staticTextImageClass(field);
    
    return (
      <div key={index} style={{ ...fieldStyle, ...borderStyle }}>
        {hasHtmlTags ? (
          // Render as HTML for rich text content with inline formatting
          <div 
            className={`whitespace-pre-wrap static-text-html ${imgClass}`} 
            style={containerStyle}
            dangerouslySetInnerHTML={{ __html: processedContent }}
          />
        ) : (
          // Render as plain text for legacy content with field-level styles
          <div 
            className={`whitespace-pre-wrap ${imgClass}`} 
            style={{
              ...containerStyle,
              fontWeight: field.fontWeight || 'normal',
              fontStyle: field.fontStyle || 'normal',
              textDecoration: field.textDecoration || 'none',
              color: field.textColor || '#000000'
            }}
          >
            {processedContent}
          </div>
        )}
      </div>
    );
  }
  
  // Divider
  if (field.type === 'divider') {
    return (
      <div key={index} style={fieldStyle}>
        <hr style={{ borderTopWidth: `${field.thickness || 1}px`, borderColor: field.color || '#e5e7eb' }} />
      </div>
    );
  }
  
  // Spacer
  if (field.type === 'spacer') {
    return (
      <div key={index} style={fieldStyle}>
        <div style={{ height: `${field.height || 20}px` }} />
      </div>
    );
  }
  
  // Section header - styled section divider
  if (field.type === 'section_header') {
    const sectionColor = field.backgroundColor || '#2563eb';
    return (
      <div key={index} style={{ ...fieldStyle, width: '100%', minHeight: '40px' }} className="mt-6 mb-3">
        {field.label ? (
          <div 
            className="text-white px-4 py-2 rounded-lg shadow-sm"
            style={{ backgroundColor: sectionColor }}
          >
            <h3 className="text-base font-semibold">{field.label}</h3>
          </div>
        ) : (
          <div 
            className="border-b-2 min-h-[8px]"
            style={{ borderColor: sectionColor }}
          />
        )}
      </div>
    );
  }
  
  // Not a layout field type
  return null;
};

export default renderLayoutField;
